import React from 'react';
import ModalBase from '../../../components/ui/ModalBase/ModalBase';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import { Button } from '../../../components/ui/Button2';
import { TypographyH4 } from '../../../components/ui/Typography';
import { Icon } from '../../../components/ui/Icon';
import styles from './DocumentAssessment.module.css';

/**
 * One gap, answering the four questions in the order a customer asks them:
 * what is missing, where in MY document, why it matters (the clause an
 * assessor cites), and how to close it.
 *
 * A gap missing any of these is not actionable, so each section renders even
 * when sparse rather than being conditionally hidden.
 *
 * ModalBase supplies only portal/escape/backdrop behaviour; the header and
 * layout below are this component's job.
 */
const SEVERITY_TONE = { high: 'error', medium: 'warning', low: 'info' };

const GapDetailModal = ({ gap, onClose }) => (
  <ModalBase
    isOpen
    onClose={onClose}
    ariaLabel={`Gap detail: ${gap.title}`}
    overlayClassName={styles.modalOverlay}
  >
    <div className={styles.modal}>
      <header className={styles.modalHeader}>
        <TypographyH4>{gap.title}</TypographyH4>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label="Close gap detail"
        >
          <Icon name="close" size="sm" />
        </Button>
      </header>

      <div className={styles.gapDetailBadges}>
        <StatusPill
          tone={SEVERITY_TONE[gap.severity] || 'neutral'}
          label={gap.severity}
          size="sm"
        />
        <StatusPill tone="neutral" label={`Clause ${gap.iso_clause || '—'}`} size="sm" />
        {gap.assigned_to && (
          <StatusPill tone="info" label={`Assigned: ${gap.assigned_to}`} size="sm" />
        )}
      </div>

      <section className={styles.gapSection}>
        <h4 className={styles.gapSectionLabel}>What is missing</h4>
        <p>{gap.detail || gap.title}</p>
      </section>

      <section className={styles.gapSection}>
        <h4 className={styles.gapSectionLabel}>Where</h4>
        <p>
          {gap.source?.document_name
            ? `${gap.source.document_name}${
                gap.source.location ? ` — ${gap.source.location}` : ''
              }`
            : 'Not found in any supplied document.'}
        </p>
      </section>

      <section className={styles.gapSection}>
        <h4 className={styles.gapSectionLabel}>Why it matters</h4>
        <p>
          {gap.iso_clause
            ? `ISO/SAE 21434 clause ${gap.iso_clause} — an assessor will raise this at audit.`
            : 'Expected by the configured assessment reference.'}
        </p>
      </section>

      <section className={styles.gapSection}>
        <h4 className={styles.gapSectionLabel}>How to close it</h4>
        <p>{gap.remediation || '—'}</p>
      </section>

      {gap.state === 'dismissed' && gap.dismissal_rationale && (
        <section className={styles.gapSection}>
          <h4 className={styles.gapSectionLabel}>Dismissed because</h4>
          <p>{gap.dismissal_rationale}</p>
        </section>
      )}
    </div>
  </ModalBase>
);

export default GapDetailModal;
